import type Phaser from 'phaser';
import type { SimEvent } from '../../sim/events';
import type { Simulation } from '../../sim/simulation';
import { TICK_RATE } from '../../sim/constants';
import { AMBIENT, audioKey, NIGHT_VOLUME, SFX, type AmbientKey, type SfxKey } from './manifest';
import { ENGINE_PROFILES } from './engineVoice';
import { Synth, type EngineKind } from './synth';

/**
 * The game's audio: a quiet ambient bed that runs everywhere, file-based sound effects, and the
 * synthesized mower. One instance for the whole game (sounds outlive scenes).
 */
class GameAudio {
  private sound: Phaser.Sound.BaseSoundManager | null = null;
  private synth: Synth | null = null;
  /** Web Audio, its output (Phaser's master), and the decoded files (for the ambient loops). */
  private ctx: AudioContext | null = null;
  private output: AudioNode | null = null;
  private audioCache: Phaser.Cache.BaseCache | null = null;
  private ambientStarted = false;
  /** The ambient loops, each with its own gain (eased, never stepped: a stepped gain clicks). */
  private readonly ambient = new Map<AmbientKey, GainNode>();
  /** Night (Lights Out): birdsong hushed, the breeze up. */
  private night = false;
  /** Gates the synthesized effects (mower, rustle, horn...) with the sound-effects setting. */
  private synthGain: GainNode | null = null;
  private effectsOn = true;
  private ambienceOn = true;
  private lastSqueak = 0;

  /** Queues every audio file on a scene's loader (call from a preload). */
  load(scene: Phaser.Scene): void {
    for (const [name, a] of Object.entries({ ...AMBIENT, ...SFX })) {
      scene.load.audio(audioKey(name), a.path);
    }
  }

  /** Hooks up the sound manager once files are loaded; starts the ambient bed when allowed. */
  init(scene: Phaser.Scene, effects: boolean, ambience: boolean): void {
    this.sound = scene.sound;
    this.audioCache = scene.cache.audio;
    const manager = scene.sound as Partial<Phaser.Sound.WebAudioSoundManager>;
    if (manager.context && manager.destination && !this.synth) {
      this.ctx = manager.context;
      this.output = manager.destination;
      this.synthGain = manager.context.createGain();
      this.synthGain.connect(manager.destination);
      this.synth = new Synth(manager.context, this.synthGain);
      this.addLimiter(manager as Phaser.Sound.WebAudioSoundManager);
    }
    this.setEnabled(effects, ambience);
    // Mobile browsers start audio locked until the first touch.
    if (scene.sound.locked) scene.sound.once('unlocked', () => this.startAmbient());
    else this.startAmbient();
  }

  /**
   * A gentle limiter on everything the game plays (owner, 2026-10-05: a faint click now and then).
   * Many sounds at once (a shower of coins, fireworks and chimes) could sum past full scale and
   * clip; this holds the peaks down softly instead.
   */
  private addLimiter(manager: Phaser.Sound.WebAudioSoundManager): void {
    const ctx = manager.context;
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -6;
    limiter.knee.value = 6;
    limiter.ratio.value = 12;
    limiter.attack.value = 0.003;
    limiter.release.value = 0.15;
    manager.masterVolumeNode.disconnect();
    manager.masterVolumeNode.connect(limiter).connect(ctx.destination);
  }

  /** Sound effects and the ambient bed are switched separately (Settings). */
  setEnabled(effects: boolean, ambience: boolean): void {
    this.effectsOn = effects;
    this.ambienceOn = ambience;
    if (this.synthGain && this.ctx) {
      this.synthGain.gain.setTargetAtTime(effects ? 1 : 0, this.ctx.currentTime, 0.02);
    }
    this.easeAmbient(0.15);
  }

  /** How loud an ambient loop should be now (night, and the ambience setting). */
  private ambientLevel(key: AmbientKey): number {
    if (!this.ambienceOn) return 0;
    return this.night ? NIGHT_VOLUME[key] : AMBIENT[key].volume;
  }

  /** Eases every ambient loop to its level over about `seconds`. */
  private easeAmbient(seconds: number): void {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    for (const [key, gain] of this.ambient) {
      gain.gain.cancelScheduledValues(now);
      gain.gain.setValueAtTime(gain.gain.value, now);
      gain.gain.setTargetAtTime(this.ambientLevel(key), now, seconds / 3);
    }
  }

  /**
   * The ambient bed: each recording looped on its own source, between its first and last audible
   * samples (a browser's MP3 decoder may pad silence onto the ends, which would open a gap and
   * click at every wrap; the files themselves are cross-faded to join seamlessly:
   * tools/seamless_loop.py). Fades in.
   */
  private startAmbient(): void {
    if (this.ambientStarted || !this.ctx || !this.output || !this.audioCache) return;
    this.ambientStarted = true;
    const now = this.ctx.currentTime;
    for (const name of Object.keys(AMBIENT) as AmbientKey[]) {
      const buffer = this.audioCache.get(audioKey(name)) as AudioBuffer | undefined;
      if (!buffer) continue;
      const [from, to] = audibleSpan(buffer);
      const src = this.ctx.createBufferSource();
      src.buffer = buffer;
      src.loop = true;
      src.loopStart = from;
      src.loopEnd = to;
      const gain = this.ctx.createGain();
      gain.gain.setValueAtTime(0, now);
      src.connect(gain).connect(this.output);
      src.start(now, from + Math.random() * (to - from));
      this.ambient.set(name, gain);
    }
    this.easeAmbient(1.5);
  }

  /**
   * Night on a lawn (Lights Out) or day again: the ambient bed eases over in about a second (the
   * birds fall quiet and the breeze rises, or back).
   */
  setNight(on: boolean): void {
    if (on === this.night) return;
    this.night = on;
    this.easeAmbient(1);
  }

  /** Mower soccer's referee: one long peep for the kick-off. */
  kickOff(): void {
    if (this.effectsOn) this.synth?.whistle([0.4]);
  }

  /** The mower's horn: the lobby's "your friend is here" (docs/FRIENDS.md). */
  honk(): void {
    this.synth?.horn();
  }

  /** The reveal screens' chime (bigger for a zone or all-stars). */
  chime(big = false): void {
    this.synth?.chime(big);
  }

  play(key: SfxKey, rate = 1): void {
    if (!this.sound || this.sound.locked || !this.effectsOn) return;
    this.sound.play(audioKey(key), { volume: SFX[key].volume, rate });
  }

  // ── Mower ────────────────────────────────────────────────────────────────────────────────

  startMower(sim: Simulation, mowerId: string): void {
    const kind: EngineKind =
      sim.mowerSpec.fuelTiles === undefined
        ? 'reel'
        : (ENGINE_PROFILES[mowerId as keyof typeof ENGINE_PROFILES] ?? ENGINE_PROFILES.push);
    this.synth?.startEngine(
      kind,
      TICK_RATE / sim.mowerSpec.ticksPerTile,
      TICK_RATE / sim.crossTicks(),
    );
  }

  /** Called every frame while mowing: pitch follows speed (gear, mud). */
  updateMower(sim: Simulation): void {
    this.synth?.setEngineSpeed(TICK_RATE / sim.crossTicks());
  }

  stopMower(): void {
    this.synth?.stopEngine();
  }

  /** Sound for one tick's worth of sim events (`sim` tells which critter a shooed one is). */
  onEvents(events: readonly SimEvent[], sim?: Simulation): void {
    for (const e of events) {
      switch (e.type) {
        case 'tileMowed': {
          // Lawn Art: each picture tile rings right or boops wrong.
          const want = sim?.level.art?.pattern[e.y * sim.grid.width + e.x] ?? 0;
          if (sim && want !== 0) {
            const right = sim.artAxisAt(e.x, e.y) === want;
            this.synth?.artTile(right, Math.round((sim.artMatch() ?? 0) * 20));
            break;
          }
          if (e.flattened) this.synth?.plop();
          else if (e.result === 'cleared') this.synth?.snap();
          else if (e.fresh || e.result === 'thinned') this.synth?.rustle();
          break;
        }
        case 'turned':
          this.synth?.tick();
          break;
        case 'pickup':
          if (e.kind === 'coin' || e.kind === 'treasure' || e.spare)
            this.play('coin', 0.95 + Math.random() * 0.1);
          else if (e.kind === 'hoard') {
            this.play('rare');
            this.chime(true);
          } else if (e.kind === 'rare') this.play('rare');
          else if (e.kind === 'gas') this.synth?.glug();
          else this.play('pickup');
          break;
        case 'crashed':
          this.play('bump');
          break;
        case 'mowerBroken':
          this.stopMower();
          this.synth?.clang();
          break;
        case 'outOfFuel':
          this.stopMower();
          this.synth?.sputter();
          break;
        case 'reserveCan':
          this.synth?.glug();
          break;
        case 'flatTire':
          this.synth?.hiss(e.saved);
          break;
        case 'treeCreak':
          this.synth?.creak();
          break;
        case 'treeFell':
          this.synth?.timber();
          break;
        case 'hiveSmashed':
          this.synth?.crunch();
          this.synth?.buzz();
          break;
        case 'regrew':
          this.synth?.regrow();
          break;
        case 'critterPoofed':
          // A squeak and a soft pop: off it goes, unharmed.
          this.play('squeak', 1.15 + Math.random() * 0.2);
          this.synth?.plop();
          break;
        case 'boostUsed':
          this.play('pickup', 1.3);
          break;
        case 'molehill':
          this.synth?.squish();
          break;
        case 'ballWarning':
          this.synth?.whistle();
          break;
        case 'ballHit':
          this.play('bump');
          break;
        case 'outmowed':
          this.stopMower();
          this.play('deny');
          break;
        case 'critterShooed': {
          // Squirrels and rabbits squeak; woodland wildlife (deer, a stag, foxes) slips away quiet.
          const kind = sim?.critters.list[e.index]?.kind;
          if (kind === 'deer' || kind === 'stag' || kind === 'fox') break;
          const now = performance.now();
          if (now - this.lastSqueak > 300) this.play('squeak', 1 + Math.random() * 0.3);
          this.lastSqueak = now;
          break;
        }
        case 'toolUsed':
          if (e.cleared.length > 0) this.play('rake');
          else this.synth?.horn();
          break;
        case 'toolDenied':
          this.play('deny');
          break;
        case 'levelComplete':
          this.stopMower();
          this.play('complete');
          break;
        case 'trampled':
          this.synth?.squish();
          break;
        case 'smashed':
          this.synth?.crunch();
          this.play('bump', 1.3);
          break;
        case 'sprinklerBroken':
          this.synth?.crunch();
          this.play('bump', 1.1);
          break;
        case 'chewed':
          this.synth?.squish();
          this.play('bump', 0.8);
          break;
        case 'revived':
          this.play('pickup', 1.1);
          break;
        case 'kicked':
          this.synth?.kick(e.squirt);
          break;
        case 'goal':
          this.synth?.roar();
          this.play('pickup', 1.2);
          break;
        case 'ballReset':
          this.synth?.plop();
          break;
        case 'halftime':
          // Two short peeps and a long one: that's the half.
          this.synth?.whistle([0.12, 0.12, 0.5]);
          break;
        case 'nudged':
          this.synth?.baa();
          if (e.penned) this.play('pickup', 0.9);
          break;
        case 'turnRejected':
          break;
      }
    }
  }
}

export const gameAudio = new GameAudio();

/** The span (seconds) between a buffer's first and last audible samples. */
function audibleSpan(buffer: AudioBuffer): [number, number] {
  const data = buffer.getChannelData(0);
  const quiet = 1e-4;
  let first = 0;
  while (first < data.length - 1 && Math.abs(data[first]!) < quiet) first++;
  let last = data.length - 1;
  while (last > first && Math.abs(data[last]!) < quiet) last--;
  return [first / buffer.sampleRate, (last + 1) / buffer.sampleRate];
}
